/**
 * AI steering maths (spec §7): turning a desired world direction into stick commands through the ship's
 * local frame, seeded Gaussian noise and look rotations. Allocation-free (D-014): module scratch only.
 */
import { clamp, inverseRotateVec, type Quat, type Vec3 } from '../core/math'
import { nextFloat, type Rng } from '../core/rng'
import { Control, Rot } from '../world/components'

const EPS = 1e-9
const HALF_PI = Math.PI / 2

// Module scratch (D-014).
const q: Quat = { x: 0, y: 0, z: 0, w: 1 }
const local: Vec3 = { x: 0, y: 0, z: 0 }

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

/** Copies ship `eid`'s rotation into `out`. */
export function loadRot(eid: number, out: Quat): Quat {
  out.x = Rot.x[eid]
  out.y = Rot.y[eid]
  out.z = Rot.z[eid]
  out.w = Rot.w[eid]
  return out
}

/** A standard normal sample (Box-Muller) from the seeded generator; consumes two draws. */
export function gaussian(rng: Rng): number {
  gaussianTo(rng, gaussScratch)
  return gaussScratch.value
}

/** `gaussianTo`'s result. A class, not a literal, so the float field stays unboxed (the `SteerArgs` pattern). */
export class GaussianOut {
  value = 0
}

const gaussScratch = new GaussianOut()

/**
 * `gaussian` into `out.value` (D-014): a float returned from a call V8 does not inline is boxed, one heap number per
 * call, so a hot caller past the inlining limit (an AI pilot's `stepEngage`) keeps its own `GaussianOut`.
 */
export function gaussianTo(rng: Rng, out: GaussianOut): void {
  const u1 = 1 - nextFloat(rng) // (0, 1]: log never sees 0
  const u2 = nextFloat(rng)
  out.value = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2)
}

/**
 * `steerTo`'s arguments and result. A class, not a literal, so its float fields keep their own unboxed
 * representation (gate-A decision): `steerTo` is over V8's inlining limit, and floats passed to or returned
 * from a call that is not inlined are boxed, one heap number each, every tick.
 */
export class SteerArgs {
  /** World direction to turn the nose toward (any length). */
  dx = 0
  dy = 0
  dz = 0
  turnGain = 0
  rollGain = 0
  /** Result: the angle between the nose and the direction (rad). */
  angle = 0
}

const steerScratch = new SteerArgs()

/**
 * `steerTo` with the arguments as numbers; returns the angle. Small enough for V8 to inline, so hot callers that
 * are inlined themselves box nothing; a hot caller that is not keeps its own `SteerArgs` and calls `steerTo`.
 */
export function steerToward(eid: number, dx: number, dy: number, dz: number, turnGain: number, rollGain: number): number {
  const a = steerScratch
  a.dx = dx
  a.dy = dy
  a.dz = dz
  a.turnGain = turnGain
  a.rollGain = rollGain
  steerTo(eid, a)
  return a.angle
}

/**
 * Writes `Control.pitch`, `yaw` and `roll` of ship `eid` to turn its nose (local −Z) toward the world
 * direction `a.dx, a.dy, a.dz` (any length) and writes the angle between the nose and that direction to
 * `a.angle` (rad).
 *
 * - Pitch and yaw are P commands on the heading error in the ship frame (`turnGain` per radian, clamped),
 *   so a target behind saturates both and the ship turns the short way round.
 * - Roll banks into the turn (`rollGain` per radian of bank error), putting the target on the nearer of
 *   the ship's up or down axis so pitch, the faster axis, does most of the turning. It is weighted by the
 *   sine of the off-nose angle, so the ship stops rolling as the target centres (no jitter dead ahead).
 */
export function steerTo(eid: number, a: SteerArgs): void {
  const turnGain = a.turnGain
  const rollGain = a.rollGain
  inverseRotateVec(loadRot(eid, q), a.dx, a.dy, a.dz, local)
  const len = Math.sqrt(local.x * local.x + local.y * local.y + local.z * local.z)
  if (len < EPS) {
    Control.pitch[eid] = 0
    Control.yaw[eid] = 0
    Control.roll[eid] = 0
    a.angle = 0
    return
  }
  const lx = local.x / len
  const ly = local.y / len
  const fwd = -local.z / len
  const lateral = Math.sqrt(lx * lx + ly * ly)

  Control.pitch[eid] = clamp(turnGain * Math.atan2(ly, fwd), -1, 1)
  Control.yaw[eid] = clamp(turnGain * Math.atan2(lx, fwd), -1, 1)

  let bank = 0
  if (lateral > EPS) {
    // Angle of the target around the nose, measured from local up toward local right (+ = roll right).
    bank = Math.atan2(lx, ly)
    if (bank > HALF_PI) bank -= Math.PI
    else if (bank < -HALF_PI) bank += Math.PI
  }
  Control.roll[eid] = clamp(rollGain * bank * lateral, -1, 1)
  a.angle = Math.atan2(lateral, fwd)
}

/**
 * The rotation that points local −Z along (fx, fy, fz) with local +Y as close to world +Y as possible
 * (world +X when the direction is vertical). Writes `out`; a zero direction gives the identity.
 */
export function lookRotation(fx: number, fy: number, fz: number, out: Quat): Quat {
  const len = Math.sqrt(fx * fx + fy * fy + fz * fz)
  if (len < EPS) {
    out.x = out.y = out.z = 0
    out.w = 1
    return out
  }
  fx /= len
  fy /= len
  fz /= len
  // Up hint: world +Y unless the direction is (nearly) vertical.
  let ux = 0
  let uy = 1
  const uz = 0
  if (Math.abs(fy) > 0.999) {
    ux = 1
    uy = 0
  }
  // right = f × up, up' = right × f, back = −f: the columns of the rotation matrix.
  let rx = fy * uz - fz * uy
  let ry = fz * ux - fx * uz
  let rz = fx * uy - fy * ux
  const rl = Math.sqrt(rx * rx + ry * ry + rz * rz)
  rx /= rl
  ry /= rl
  rz /= rl
  const vx = ry * fz - rz * fy
  const vy = rz * fx - rx * fz
  const vz = rx * fy - ry * fx
  const m00 = rx
  const m01 = vx
  const m02 = -fx
  const m10 = ry
  const m11 = vy
  const m12 = -fy
  const m20 = rz
  const m21 = vz
  const m22 = -fz
  const trace = m00 + m11 + m22
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1)
    out.w = 0.25 / s
    out.x = (m21 - m12) * s
    out.y = (m02 - m20) * s
    out.z = (m10 - m01) * s
  } else if (m00 > m11 && m00 > m22) {
    const s = 2 * Math.sqrt(1 + m00 - m11 - m22)
    out.w = (m21 - m12) / s
    out.x = 0.25 * s
    out.y = (m01 + m10) / s
    out.z = (m02 + m20) / s
  } else if (m11 > m22) {
    const s = 2 * Math.sqrt(1 + m11 - m00 - m22)
    out.w = (m02 - m20) / s
    out.x = (m01 + m10) / s
    out.y = 0.25 * s
    out.z = (m12 + m21) / s
  } else {
    const s = 2 * Math.sqrt(1 + m22 - m00 - m11)
    out.w = (m10 - m01) / s
    out.x = (m02 + m20) / s
    out.y = (m12 + m21) / s
    out.z = 0.25 * s
  }
  return out
}
